import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  private publicKeyPem!: string;
  private publicKey!: CryptoKey;
  private apiUrl = `${environment.apiUrl}/api/user/publickey`;

  constructor(private http: HttpClient) {}

  /** Load public key on app startup */
  async loadPublicKey(): Promise<void> {
    try {
      // Fetch the PEM key as plain text
      this.publicKeyPem = await this.http
        .get(this.apiUrl, { responseType: 'text' })
        .toPromise();

      // Fix escaped newlines if they exist
      this.publicKeyPem = this.publicKeyPem.replace(/\\n/g, '\n');

      // Convert PEM to CryptoKey
      this.publicKey = await this.importPublicKey(this.publicKeyPem);

      console.log('[Security] Public Key Loaded');
    } catch (err) {
      console.error('[Security] Failed to load public key', err);
      throw err;
    }
  }

  /** Convert PEM public key → CryptoKey */
  private async importPublicKey(pem: string): Promise<CryptoKey> {
    // Remove header/footer and line breaks
    const cleanPem = pem
      .replace('"-----BEGIN PUBLIC KEY-----', '')
      .replace('-----END PUBLIC KEY-----"', '')
      .replace(/\s+/g, '');

    const binaryDer = atob(cleanPem);
    const binaryDerBuffer = new Uint8Array(binaryDer.length);

    for (let i = 0; i < binaryDer.length; i++) {
      binaryDerBuffer[i] = binaryDer.charCodeAt(i);
    }

    return crypto.subtle.importKey(
      'spki',
      binaryDerBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256'
      },
      false,
      ['encrypt']
    );
  }

  /** Encrypt a field (like password) using RSA-OAEP + SHA-256 */
  async encrypt(value: string): Promise<string> {
    if (!this.publicKey) {
      throw new Error('Public key not loaded');
    }

    const encoded = new TextEncoder().encode(value);

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP'
      },
      this.publicKey,
      encoded
    );

    // Convert ArrayBuffer → Base64
    return this.arrayBufferToBase64(encrypted);
  }

  /** Helper to convert ArrayBuffer to Base64 */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';

    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
  }
}

